import { useCallback, useState } from 'react'

import type { WorkspaceViewMode } from './WorkspaceViewSwitch'

export const WORKSPACE_VIEW_MODE_STORAGE_KEY = 'dreamwright:workspace-view-mode:v1'

const DEFAULT_WORKSPACE_VIEW_MODE: WorkspaceViewMode = 'canvas'

function parseStoredWorkspaceViewMode(value: string | null): WorkspaceViewMode | null {
  if (value === JSON.stringify('canvas')) return 'canvas'
  if (value === JSON.stringify('simplified')) return 'simplified'
  return null
}

export function readWorkspaceViewMode(): WorkspaceViewMode {
  if (typeof window === 'undefined') return DEFAULT_WORKSPACE_VIEW_MODE

  try {
    const stored = window.localStorage.getItem(WORKSPACE_VIEW_MODE_STORAGE_KEY)
    return parseStoredWorkspaceViewMode(stored) ?? DEFAULT_WORKSPACE_VIEW_MODE
  } catch {
    return DEFAULT_WORKSPACE_VIEW_MODE
  }
}

function writeWorkspaceViewMode(value: WorkspaceViewMode): void {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(WORKSPACE_VIEW_MODE_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Storage can be unavailable in hardened browser contexts. Keep the
    // in-memory preference working for the current session in that case.
  }
}

/** Browser-local preference that survives reloads and later login sessions. */
export function useWorkspaceViewPreference(): readonly [
  WorkspaceViewMode,
  (value: WorkspaceViewMode) => void,
] {
  const [viewMode, setViewModeState] = useState<WorkspaceViewMode>(readWorkspaceViewMode)

  const setViewMode = useCallback((next: WorkspaceViewMode) => {
    writeWorkspaceViewMode(next)
    setViewModeState(next)
  }, [])

  const preference: readonly [WorkspaceViewMode, (value: WorkspaceViewMode) => void] = [
    viewMode,
    setViewMode,
  ]
  return preference
}
